import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { rosterApi, useSearchMasterRosterQuery, useAddMemberMutation } from '../store/rosterApi'
import { showErrorNotification } from 'Shared/helpers'
import ImportMasterRosterRow from './ImportMasterRosterRow'
import SearchInput from './SearchInput'

const t = (key, options) => (!window.I18n ? key : window.I18n.t(key, options))

const ImportMasterRosterPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch()
  const [localSearch, setLocalSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [page, setPage] = useState(1)
  const [addingIds, setAddingIds] = useState(new Set())
  const [failedIds, setFailedIds] = useState(new Set())
  const [addedIds, setAddedIds] = useState(new Set())

  // Debounce the term (RTK Query has none); a new term restarts paging.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearch)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [localSearch])

  const { data, isFetching } = useSearchMasterRosterQuery(
    { search: debouncedSearch, page },
    { skip: !isOpen }
  )

  const [addMember] = useAddMemberMutation()

  const handleClose = () => {
    setLocalSearch('')
    setDebouncedSearch('')
    setPage(1)
    setAddingIds(new Set())
    setFailedIds(new Set())
    setAddedIds(new Set())
    // Drop the accumulated pages; they still list the golfers added during this visit.
    dispatch(rosterApi.util.invalidateTags(['MasterRoster']))
    onClose()
  }

  const handleAdd = async (memberId) => {
    setAddingIds((prev) => new Set(prev).add(memberId))
    try {
      await addMember(memberId).unwrap()
      setAddedIds((prev) => new Set(prev).add(memberId))
    } catch (error) {
      showErrorNotification(error?.error || error?.message || t('customer_groups.groups.members.import_panel.add_error'))
      setFailedIds((prev) => new Set(prev).add(memberId))
    } finally {
      setAddingIds((prev) => new Set([ ...prev ].filter((id) => id !== memberId)))
    }
  }

  if (!isOpen) return null

  const members = (data?.members ?? []).filter((member) => !addedIds.has(member.id))
  const totalEntries = Math.max((data?.total_entries ?? 0) - addedIds.size, 0)
  const hasMorePages = (data?.current_page ?? 1) < (data?.total_pages ?? 1)

  return (
    <>
      <div className='import-panel-overlay' onClick={handleClose} />

      <div className='import-panel'>
        <div className='import-panel__header'>
          <div>
            <h4 className='import-panel__title'>
              {t('customer_groups.groups.members.import_panel.title')}
            </h4>
            <p className='import-panel__subtitle'>
              {t('customer_groups.groups.members.import_panel.subtitle')}
            </p>
          </div>
          <button className='import-panel__close' onClick={handleClose} aria-label={t('customer_groups.groups.members.member_modal.close')}>
            &times;
          </button>
        </div>

        <div className='import-panel__search-wrap'>
          <SearchInput
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            onClear={() => setLocalSearch('')}
            placeholder={t('customer_groups.groups.members.import_panel.search_placeholder')}
            autoFocus
          />
        </div>

        {totalEntries > 0 && (
          <div className='import-panel__count' aria-live='polite'>
            {t('customer_groups.groups.members.import_panel.showing_count', {
              shown: members.length,
              total: totalEntries,
            })}
          </div>
        )}

        <div className='import-panel__results'>
          {members.map((member) => (
            <ImportMasterRosterRow
              key={member.id}
              member={member}
              isAdding={addingIds.has(member.id)}
              isFailed={failedIds.has(member.id)}
              onAdd={handleAdd}
            />
          ))}

          {isFetching && (
            <div className='import-panel__spinner'>
              <i className='fa fa-spinner fa-spin' />
            </div>
          )}

          {!isFetching && hasMorePages && (
            <div className='import-panel__load-more'>
              <button
                className='btn btn-sm btn-default'
                onClick={() => setPage(data.current_page + 1)}
              >
                {t('customer_groups.groups.members.import_panel.load_more')}
              </button>
            </div>
          )}

          {!isFetching && members.length === 0 && (
            <div className='import-panel__empty'>
              {debouncedSearch.length >= 1
                ? t('customer_groups.groups.members.import_panel.no_results')
                : t('customer_groups.groups.members.import_panel.no_members')}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

ImportMasterRosterPanel.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default ImportMasterRosterPanel
